/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Feb 20 2024
 **/

/* eslint-disable @typescript-eslint/no-unused-vars */

import { useCallback } from "react";
import { Updater, useQueryClient } from "@tanstack/react-query";

import { useStompManager } from "@/frameworks/libraries/network";
import { Configure } from "@/frameworks/libraries/utilities";
import { Properties } from "@/frameworks/libraries/properties";
import { isMessageFromMe, MessageList } from "@/frameworks/core/chat";

import { Topics } from "@/frameworks/core/commons";
import { useChat } from "@/frameworks/core/API";
import { useChatRoomListActions } from "../../useChatRoomList";

type SpaceChatListMap = Map<string, WebServices.SpaceChat.List.Data[number]>;
type DMListMap = Map<string, WebServices.DirectMessage.List.Data[number]>;
type GroupSpaceChatListMap = Map<string, WebServices.SpaceChat.GroupedList.Data>;
type RoomViewListMap = Map<string, WebServices.Chat.RoomView.Data>;

export default function useChatManagerHandler() {
	const queryClient = useQueryClient();

	const setChatRoomList = (updater: Updater<DMListMap | undefined, DMListMap | undefined>) => {
		queryClient.setQueryData<DMListMap>(["getDMList"], updater);
	};
	const setSpaceChatRoomList = (updater: Updater<SpaceChatListMap | undefined, SpaceChatListMap | undefined>) => {
		queryClient.setQueryData<SpaceChatListMap>(["getSpaceChatList"], updater);
	};
	const setGroupSpaceChatRoomList = (
		updater: Updater<GroupSpaceChatListMap | undefined, GroupSpaceChatListMap | undefined>
	) => {
		queryClient.setQueryData<GroupSpaceChatListMap>(["getGroupSpaceChatList"], updater);
	};
	const setRoomViewList = (updater: Updater<RoomViewListMap | undefined, RoomViewListMap | undefined>) => {
		queryClient.setQueryData<RoomViewListMap>(["getRoomView"], updater);
	};

	const stompNetwork = useStompManager();
	const chatRoomListActions = useChatRoomListActions();
	const chatAPI = useChat();

	const isSpace = useCallback((roomID: string) => {
		return typeof chatRoomListActions.getDMRoom(roomID) === "undefined";
	}, []);

	const roomCreated = async (payload: ChatServices.Server.RoomCreated.Data) => {
		const { roomInfo, members } = payload;
		const spaceID = roomInfo.spaceId;
		if (spaceID) {
			// 스페이스 룸생성
			const createRoomData = (await chatAPI.space.inquiry.execute({ spaceId: spaceID })).data;

			// setGroupSpaceChatRoomList 와 setSpaceChatRoomList 를 따로 나눈 이유는
			// 신규 기획 요구사항으로 인하여 spaceChatList를 Grouping하여야 하며,
			// Grouping 된 api 의 정보를 기반으로 spaceChatList를 Mapping 시켜 사용하기 위함. ( 이렇게 하지 않으면 CRUD 경우 범위가 너무 커짐 )

			// 그룹 스페이스 채팅 리스트를 재정의 한다.
			setGroupSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const myID = String(Properties.property.user?.myRoomId);
				const prevGroupSpaceChat = prev.get(myID) as WebServices.SpaceChat.GroupedList.Data;

				// 내 마이룸에 대한 리스트는 불변이기 때문에 이전 값을 유지한다.
				if (prevGroupSpaceChat.myRoomChat?.spaceId === spaceID) return prev;

				// 생성되어있는 그룹 스페이스의 채널룸 생성시 group에 대한 list는 변화시키지 않는다.
				if (prevGroupSpaceChat.groupedSpaceChats.find((item) => item.groupId === createRoomData?.groupId))
					return prev;

				const createData = {
					groupId: createRoomData?.groupId,
					groupName: createRoomData?.groupName,
					spaceChats: [createRoomData]
				} as WebServices.SpaceChat.GroupedList.Data["groupedSpaceChats"][number];

				prev.set(myID, {
					...prevGroupSpaceChat,
					groupedSpaceChats: prevGroupSpaceChat.groupedSpaceChats.concat(createData)
				});

				return prev;
			});

			// 스패이스 채팅 리스트를 재정의한다.
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const space = prev.get(spaceID);
				if (!space) {
					// 그룹 생성하여 스페이스의 기본 룸 생성시
					return prev.set(spaceID, createRoomData as WebServices.SpaceChat.List.Data[number]);
				}
				// 스페이스챗의 채널룸 생성시
				space?.rooms.splice(1, 0, {
					...roomInfo,
					ownerId: roomInfo.ownerId,
					inVoiceChat: false,
					roomType: roomInfo.roomType as WebServices.SpaceChat.List.Data[number]["rooms"][number]["roomType"],
					roomName: roomInfo.name || "",
					createdByMe: Configure.userID === roomInfo.createdBy,
					deleted: false,
					myRoom: false,
					notificationType: "ALL",
					hasNewMessage: false,
					pinned: false,
					roomId: roomInfo.roomId,
					hide: false,
					users: members.map((member) => ({
						id: member.id,
						name: member.name,
						profileImagePath: member.profileImagePath
					})),
					roomJoined: payload.members.some((v) => v.id === Configure.userID),
					alone: members.length === 1,
					createdTime: 0 // TODO 확인 후 적절한 값을 넣어야 합니다.
				});
				prev.set(spaceID, space);
				return new Map(prev);
			});
		} else {
			// DM 룸생성
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const roomJoined = Boolean(members.find((v) => v.id === Configure.userID));

				const newChatRoomList = new Map();
				newChatRoomList.set(roomInfo.roomId, {
					roomId: roomInfo.roomId,
					hide: Boolean(roomInfo.createdBy !== Configure.userID),
					roomJoined,
					hasNewMessage: false,
					createdTime: Date.now(),
					users: members.map((member) => ({
						id: member.id,
						name: member.name,
						profileImagePath: member.profileImagePath
					}))
				} as WebServices.DirectMessage.List.Data[number]);

				prev.forEach((value, key) => {
					if (key !== roomInfo.roomId) {
						newChatRoomList.set(key, value);
					}
				});

				return newChatRoomList;
			});
		}
	};

	const addSpaceChannel = (spaceID: string) => {
		chatRoomListActions.addSpaceChannel.mutate(spaceID);
	};

	const deleteSpaceChannel = (spaceID: string) => {
		chatRoomListActions.deleteSpaceChannel.mutate(spaceID);
	};

	const groupDelete = ({ groupID, spaceIDs }: { groupID: string; spaceIDs: string[] }) => {
		// 그룹스페이스 채팅 리스트에 대한 delete처리와 스페이스 채팅의 delete처리
		chatRoomListActions.deleteGroupSpaceChannel.mutate(groupID);
		spaceIDs?.forEach((spaceID) => {
			chatRoomListActions.deleteSpaceChannel.mutate(spaceID);
		});
	};

	const deleteSpaceChatRoom = (roomID: string, spaceID: string, deleteSendUser: string) => {
		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			const space = prev.get(spaceID);
			if (!space) return new Map(prev);
			const updatedRoomInfo = space.rooms
				.map((room) =>
					room.roomId === roomID
						? {
								...room,
								deleted: true,
								roomJoined:
									Configure.userID === deleteSendUser
										? false
										: space.spaceAuthority === "SPACE_ADMIN" && !room.roomJoined
											? false
											: true
							}
						: room
				)
				.filter((room) => room.roomJoined);
			const updateSpace = {
				...space,
				rooms: updatedRoomInfo
			};

			prev.set(spaceID, updateSpace);
			return new Map(prev);
		});
	};

	const leaveSpaceChatRoom = (roomID: string, spaceID: string, leaveUserID?: string) => {
		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			const space = prev.get(spaceID);
			if (!space) return new Map(prev);
			const updatedRoomInfo = space.rooms
				.map((room) =>
					room.roomId === roomID
						? {
								...room,
								roomJoined:
									Configure.userID === leaveUserID
										? false
										: space.spaceAuthority === "SPACE_ADMIN" && !room.roomJoined
											? false
											: true
							}
						: room
				)
				.filter((room) => room.roomJoined);

			const updateSpace = {
				...space,
				rooms: updatedRoomInfo
			};
			prev.set(spaceID, updateSpace);
			return new Map(prev);
		});
	};

	const updateNotification = ({
		roomID,
		spaceID,
		notificationType
	}: {
		roomID: string;
		spaceID?: string;
		notificationType: "ALL" | "ONLY_MENTION" | "OFF";
	}) => {
		if (spaceID) {
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const space = prev.get(spaceID);
				if (!space) return prev;
				const room = space.rooms.find((room) => room.roomId === roomID);
				if (!room) return prev;
				room.notificationType = notificationType;
				return new Map(prev);
			});
		} else {
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const room = prev.get(roomID);
				if (!room) return prev;
				room.notificationType = notificationType;
				return new Map(prev);
			});
		}
	};

	const memberAdded = (payload: ChatServices.Server.MemberAdded.Data) => {
		const { roomInfo, members } = payload;
		if (payload.addedMemberIds.includes(Configure.userID)) {
			roomCreated({ roomInfo, members });
			if (!stompNetwork.hasTopics([Topics.Room(payload.roomInfo.roomId)])) {
				stompNetwork.subscribe(Topics.Room(payload.roomInfo.roomId));
			}
		} else {
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const prevRoom = prev.get(payload.roomInfo.roomId);
				if (!prevRoom) return prev;
				prev.set(payload.roomInfo.roomId, {
					...prevRoom,
					users: payload.members
				});
				return new Map(prev);
			});
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				if (!roomInfo.spaceId || !roomInfo.roomId) return prev;

				const space = prev.get(roomInfo.spaceId);
				if (!space) return prev;

				const updatedRoomsMember = space.rooms.map((room) => {
					if (room.roomId !== roomInfo.roomId) return room;

					const existingUserIds = new Set(room.users.map((u) => u.id));

					const newUsers = members
						.filter((member) => !existingUserIds.has(member.id))
						.map((member) => ({
							id: member.id,
							name: member.name,
							profileImagePath: member.profileImagePath
						}));

					return {
						...room,
						users: [...room.users, ...newUsers]
					};
				});

				const updatedSpace = {
					...space,
					rooms: updatedRoomsMember
				};

				prev.set(roomInfo.spaceId, updatedSpace);
				return new Map(prev);
			});
		}
	};

	const roomDeleted = (payload: ChatServices.Server.RoomDeleted.Data) => {
		deleteSpaceChatRoom(payload.roomId, payload.spaceId, payload.senderId);
		leaveSpaceChatRoom(payload.roomId, payload.spaceId);
		if (!stompNetwork.hasTopics([Topics.Room(payload.roomId)])) {
			stompNetwork.unsubscribe(Topics.Room(payload.roomId));
		}
	};

	const messageSent = async (payload: ChatServices.Server.MessageSent.Data) => {
		const { spaceId, roomId } = payload;
		let result: MessageList[number]["vote"];

		if (JSON.parse(payload.message).type === "vote") {
			const { data } = await chatAPI.vote.detail.execute({
				roomId: roomId,
				voteId: String(payload.voteId)
			});

			result = data as MessageList[number]["vote"];
		}

		JSON.parse(payload.message).elements.map((item: { type: string }) => {
			if (item.type?.includes("link")) {
				queryClient.refetchQueries({
					queryKey: ["folderFileList", { id: "All" }]
				});
			}
		});

		if (payload.thread) return;
		if (spaceId) {
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const space = prev.get(spaceId);
				if (!space) return prev;

				const prevRoom = space.rooms.find((room) => room.roomId === roomId);
				if (!prevRoom) return prev;
				if (!prevRoom) return new Map(prev);
				return new Map(prev).set(spaceId, {
					...space,
					rooms: space.rooms.map((room) => {
						if (room.roomId === roomId) {
							return {
								...room,
								messageLastSentTime: payload.timestamp,
								hasNewMessage: !isMessageFromMe(payload),
								voteNotice: {
									...room.voteNotice,
									message: payload.message,
									vote: result || room.voteNotice?.vote
								} as WebServices.Chat.GetRoom.Data["voteNotice"]
							};
						}
						return room;
					})
				});
			});
		} else {
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const roomID = payload.roomId as string;
				const prevRoom = prev.get(roomID);
				if (!prevRoom) return new Map(prev);
				return new Map(prev).set(roomID, {
					...prevRoom,
					hide: false,
					lastSentMessage: {
						...prevRoom.lastSentMessage,
						files: payload.files,
						id: payload.messageId,
						message: payload.message,
						senderId: payload.sender.id,
						sentTime: payload.timestamp
					},
					hasNewMessage: !isMessageFromMe(payload),
					messageLastSentTime: payload.timestamp,
					voteNotice: {
						...prevRoom.voteNotice,
						message: payload.message,
						vote: result || prevRoom.voteNotice?.vote
					}
				} as WebServices.DirectMessage.List.Data[number]);
			});
		}
	};

	const memberLeft = (payload: ChatServices.Server.MemberLeft.Data) => {
		leaveSpaceChatRoom(payload.roomId, payload.spaceId, payload.leftMemberId);
	};

	const memberNotificationUpdated = (payload: ChatServices.Server.MemberNotificationUpdated.Data) => {
		// 나의 알림 설정이 변경이 되었을 경우만 업데이트 한다.
		if (payload.memberId === Configure.userID) {
			updateNotification({
				roomID: payload.roomId,
				notificationType: payload.notificationType,
				spaceID: payload?.spaceId
			});
		}
	};

	const voiceCallStatusUpdated = (payload: ChatServices.Server.VoiceCallStatusUpdated.Data) => {
		const { roomId, type, spaceId } = payload;
		if (spaceId) {
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const space = prev.get(spaceId);
				if (!space) return prev;
				const room = space.rooms.find((room) => room.roomId === roomId);
				if (!room) return prev;
				room.inVoiceChat = type === "START";
				return new Map(prev);
			});
		} else {
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const prevRoom = prev.get(roomId);
				if (!prevRoom) return prev;
				prev.set(roomId, {
					...prevRoom,
					inVoiceChat: type === "START"
				});
				return new Map(prev);
			});
		}
	};

	const roomNameUpdated = (payload: ChatServices.Server.RoomNameUpdated.Data) => {
		const { spaceId, roomId, roomName } = payload;
		if (spaceId) {
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const space = prev.get(spaceId);
				if (!space) return prev;
				const room = space.rooms.find((room) => room.roomId === roomId);
				if (!room) return prev;
				room.roomName = roomName;
				return new Map(prev);
			});
		} else {
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const prevRoom = prev.get(roomId);
				if (!prevRoom) return prev;
				prev.set(roomId, {
					...prevRoom,
					roomName: roomName
				});
				return new Map(prev);
			});
		}
	};

	const messageRead = (payload: ChatServices.Server.MessageRead.Data) => {
		const { roomId } = payload;

		if (isSpace(roomId)) {
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const prevSpace = [...prev.values()].find((space) =>
					space.rooms.find((room) => room.roomId === roomId)
				);
				if (!prevSpace) return new Map(prev);
				const room = prevSpace.rooms.find((room) => room.roomId === roomId);
				if (!room) return new Map(prev);
				prev.set(prevSpace.spaceId, {
					...prevSpace,
					rooms: prevSpace.rooms.map((v) => {
						if (v.roomId === roomId) {
							return { ...v, hasNewMessage: false, messageLastReadId: payload.messageLastReadId };
						}
						return { ...v };
					})
				});
				return new Map(prev);
			});
		} else {
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const prevRoom = prev.get(roomId);

				if (!prevRoom || !prevRoom.hasNewMessage) return prev;
				prev.set(roomId, {
					...prevRoom,
					hasNewMessage: false,
					lastSentMessage: {
						...prevRoom.lastSentMessage!,
						id: payload.messageLastReadId
					}
				});

				return new Map(prev);
			});
		}
	};

	const messageReadAll = () => {
		//Space
		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			const updated = new Map(prev);
			for (const [key, value] of updated) {
				const updatedRooms = value.rooms.map((room) => ({
					...room,
					hasNewMessage: false,
					messageLastReadId: room.lastSentMessage?.id
				}));
				updated.set(key, {
					...value,
					rooms: updatedRooms
				});
			}
			return updated;
		});
		//DM
		setChatRoomList((prev) => {
			if (!prev) return prev;
			const updated = new Map(prev);
			for (const [key, value] of updated) {
				updated.set(key, {
					...value,
					hasNewMessage: false,
					lastSentMessage: {
						...value.lastSentMessage!,
						id: value.lastSentMessage!.id
					}
				});
			}
			return updated;
		});
	};

	const memberRoomPinned = (payload: ChatServices.Server.MemberRoomPinned.Data) => {
		setChatRoomList((prev) => {
			if (!prev) return prev;
			const prevRoom = prev.get(payload.roomId);
			if (!prevRoom) return prev;
			prev.set(payload.roomId, {
				...prevRoom,
				pinned: payload.roomPinned,
				pinUpdatedTime: Number(payload.timestamp)
			});
			return new Map(prev);
		});
	};

	const memberRoomHided = (payload: ChatServices.Server.MemberRoomHided.Data) => {
		setChatRoomList((prev) => {
			if (!prev) return prev;
			const prevRoom = prev.get(payload.roomId);
			if (!prevRoom) return prev;
			prev.set(payload.roomId, {
				...prevRoom,
				hide: payload.roomHide
			});
			return new Map(prev);
		});
	};

	const roomNoticeUpdate = async (payload: ChatServices.Server.MessageUpdated.Data) => {
		const { data } = await chatAPI.vote.detail.execute({
			roomId: payload.roomId,
			voteId: String(payload.voteId)
		});

		const result = data as MessageList[number]["vote"];

		if (payload.spaceId) {
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const space = prev.get(payload.spaceId);
				if (!space) return prev;
				const room = space.rooms.find((room) => room.roomId === payload.roomId);
				if (!room) return prev;

				room.voteNotice = {
					files: [],
					message: payload.message,
					messageId: payload.messageId,
					noticeType: "VOTE",
					roomNoticeId: String(room.voteNotice?.roomNoticeId),
					timestamp: Number(room.voteNotice?.timestamp),
					userId: room.voteNotice?.userName,
					userName: room.voteNotice?.userName,
					vote: result
				};

				return new Map(prev);
			});
		} else {
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const prevRoom = prev.get(payload.roomId);
				if (!prevRoom) return prev;
				if (prevRoom.voteNotice?.vote?.voteId !== payload.voteId) return prev;

				prev.set(payload.roomId, {
					...prevRoom,
					voteNotice: {
						files: [],
						message: payload.message,
						messageId: payload.messageId,
						noticeType: "VOTE",
						roomNoticeId: String(prevRoom.voteNotice?.roomNoticeId),
						timestamp: Number(prevRoom.voteNotice?.timestamp),
						userId: prevRoom.voteNotice?.userName,
						userName: prevRoom.voteNotice?.userName,
						vote: result
					}
				});

				return new Map(prev);
			});
		}
	};

	const roomNoticeNotified = async (payload: ChatServices.Server.RoomNoticeNotified.Data) => {
		let result: MessageList[number]["vote"];

		if (payload.noticeType === "VOTE" && payload.notified) {
			const { data } = await chatAPI.vote.detail.execute({
				roomId: payload.roomId,
				voteId: String(payload.notice.voteId)
			});
			result = data as MessageList[number]["vote"];
		}

		if (payload.spaceId) {
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const space = prev.get(payload.spaceId);
				if (!space) return prev;
				const room = space.rooms.find((room) => room.roomId === payload.roomId);
				if (!room) return prev;
				if (!payload.notified) {
					if (payload.noticeType === "VOTE") delete room.voteNotice;
					else delete room.notice;
					return new Map(prev);
				}

				if (payload.noticeType === "VOTE") {
					room.voteNotice = {
						files: [],
						message: payload.notice.message,
						messageId: payload.messageId,
						noticeType: payload.noticeType,
						roomNoticeId: payload.notice.roomNoticeId,
						timestamp: payload.notice.timestamp,
						userId: payload.notice.memberId,
						userName: payload.notice.memberName,
						vote: {
							...result,
							end: !payload.notified
						} as MessageList[number]["vote"]
					};
				} else {
					room.notice = {
						roomNoticeId: payload.notice.roomNoticeId,
						message: payload.notice.message,
						messageId: payload.messageId,
						timestamp: payload.notice.timestamp,
						userId: payload.notice.memberId,
						userName: payload.notice.memberName,
						noticeType: payload.noticeType
					};
				}

				return new Map(prev);
			});
		} else {
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const prevRoom = prev.get(payload.roomId);
				if (!prevRoom) return prev;
				if (!payload.notified) {
					if (payload.noticeType === "VOTE") delete prevRoom.voteNotice;
					else delete prevRoom.notice;
					prev.set(payload.roomId, {
						...prevRoom
					});
					return new Map(prev);
				}

				const data =
					payload.noticeType === "VOTE"
						? {
								...prevRoom,
								voteNotice: {
									files: [],
									message: payload.notice.message,
									messageId: payload.messageId,
									noticeType: "VOTE" as ChatServices.Server.RoomNoticeNotified.Data["noticeType"],
									roomNoticeId: payload.notice.roomNoticeId,
									timestamp: payload.notice.timestamp,
									userId: payload.notice.memberId,
									userName: payload.notice.memberName,
									vote: {
										...result,
										end: !payload.notified
									} as MessageList[number]["vote"]
								}
							}
						: {
								...prevRoom,
								notice: {
									roomNoticeId: payload.notice.roomNoticeId,
									message: payload.notice.message,
									messageId: payload.messageId,
									timestamp: payload.notice.timestamp,
									userId: payload.notice.memberId,
									userName: payload.notice.memberName,
									noticeType: "GENERAL" as ChatServices.Server.RoomNoticeNotified.Data["noticeType"]
								}
							};

				prev.set(payload.roomId, data);

				return new Map(prev);
			});
		}
	};

	const userUpdated = (payload: PushServices.User.UserUpdated.Data) => {
		// setChatRoomList((prev) => {
		// 	if (!prev) return prev;
		// 	prev.forEach((room) => {
		// 		const newUsers = room.users.map((user) => {
		// 			if (user.id === payload.userInfo.id) {
		// 				return {
		// 					...user,
		// 					activeStatus: payload.updatedInfo.activeStatus || user.activeStatus || "ACTIVE",
		// 					colorProfile: payload.updatedInfo.colorProfile,
		// 					name: payload.updatedInfo.name || user.name,
		// 					profileImagePath: payload.updatedInfo.profileImagePath
		// 				};
		// 			}
		// 			return user;
		// 		});
		// 		prev.set(room.roomId, { ...room, users: newUsers });
		// 	});
		// 	return new Map(prev);
		// });
		//
		// setSpaceChatRoomList((prev) => {
		// 	if (!prev) return prev;
		// 	prev.forEach((space) => {
		// 		const newRooms = space.rooms.map((room) => {
		// 			return {
		// 				...room,
		// 				users: room.users.map((user) => {
		// 					if (user.id === payload.userInfo.id) {
		// 						return {
		// 							...user,
		// 							activeStatus: payload.updatedInfo.activeStatus || user.activeStatus || "ACTIVE",
		// 							colorProfile: payload.updatedInfo.colorProfile,
		// 							name: payload.updatedInfo.name || user.name,
		// 							profileImagePath: payload.updatedInfo.profileImagePath
		// 						};
		// 					}
		// 					return user;
		// 				})
		// 			};
		// 		});
		// 		prev.set(space.spaceId, { ...space, rooms: newRooms });
		// 	});
		// 	return new Map(prev);
		// });
	};

	const groupWithdrawn = (payload: PushServices.Group.GroupWithdrawn.Data) => {
		// 현재 그룹 탈퇴시 memberLeft와 groupWithdrawn 가 같이 옴.... spaceWithdrawn 도 동일...
		setGroupSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			const result: Map<string, WebServices.SpaceChat.GroupedList.Data> = new Map();
			const myID = String(Properties.property.user?.myRoomId);
			const prevData = prev?.get(myID) as WebServices.SpaceChat.GroupedList.Data;

			// 그룹 탈퇴시 그룹채팅리스트의 해당 그룹 삭제
			result.set(myID, {
				...prevData,
				groupedSpaceChats: prevData.groupedSpaceChats.filter((item) => item.groupId !== payload.groupInfo.id)
			});
			return result;
		});

		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			prev.forEach((v) => {
				if (v.groupId === payload.groupInfo.id) {
					prev.delete(v.spaceId);
				}
			});
			return new Map(prev);
		});
	};

	const userBlock = (payload: PushServices.User.UserBlock.Data) => {
		const hasUser = (room: WebServices.Chat.GetRoom.Data) => {
			if (room.users.some((v) => v.id === payload.userProfile.id)) {
				return true;
			}
			return false;
		};

		setChatRoomList((prev) => {
			if (!prev) return prev;
			prev.forEach((room) => {
				if (hasUser(room)) {
					const newUsers = room.users.map((v) => {
						if (v.id === payload.userProfile.id) {
							return { ...v, blocked: payload.blocked };
						}
						return v;
					});
					prev.set(room.roomId, { ...room, users: newUsers });
				}
			});
			return new Map(prev);
		});
	};

	const spaceInfoUpdated = (payload: PushServices.Space.SpaceInfoUpdated.Data) => {
		const { spaceInfo } = payload;
		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			prev.forEach((v) => {
				if (v.spaceId === spaceInfo.id) {
					const prevSpaceInfo = prev.get(v.spaceId);
					if (prevSpaceInfo) {
						prev.set(v.spaceId, {
							...prevSpaceInfo,
							spaceName: spaceInfo.name,
							thumbnailPath: spaceInfo.thumbnailPath
						});
					}
				}
			});
			return new Map(prev);
		});
	};

	const myRoomUpdated = (payload: PushServices.Space.MyRoomUpdated.Data) => {
		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			prev.forEach((v) => {
				if (v.spaceId === payload.id) {
					const prevSpaceInfo = prev.get(v.spaceId);
					if (prevSpaceInfo) {
						prev.set(v.spaceId, {
							...prevSpaceInfo,
							spaceName: payload.name
						});
					}
				}
			});
			return new Map(prev);
		});
	};

	const spaceCreated = (payload: PushServices.Space.SpaceCreated.Data) => {
		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			prev.set(payload.createdSpaceInfo.id, {
				myRoom: false,
				spaceId: payload.createdSpaceInfo.id,
				spaceName: payload.createdSpaceInfo.name,
				thumbnailPath: payload.createdSpaceInfo.thumbnailPath,
				useSpaceChatNotification: true,
				rooms: [],
				pinned: false
			});
			return new Map(prev);
		});
	};

	const spaceDeleted = (payload: PushServices.Space.SpaceDeleted.Data) => {
		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			prev.delete(payload.spaceInfo.id);
			return new Map(prev);
		});
	};

	const spaceInviteProcessed = (payload: PushServices.Space.SpaceInviteProcessed.Data) => {
		setSpaceChatRoomList((prev) => {
			if (!prev) return prev;
			if (!payload.approved) return prev;
			prev.set(payload.spaceInfo.id, {
				myRoom: false,
				spaceId: payload.spaceInfo.id,
				spaceName: payload.spaceInfo.name,
				thumbnailPath: payload.spaceInfo.thumbnailPath,
				useSpaceChatNotification: true,
				rooms: [],
				pinned: false
			});
			return new Map(prev);
		});
	};

	const roomViewUpdated = (payload: ChatServices.Server.RoomViewed.Data) => {
		setRoomViewList((prev) => {
			if (!prev) return prev;
			const prevRoomViewList = prev.get(Configure.userID) as WebServices.Chat.RoomView.Data;

			if (prevRoomViewList) {
				if (payload.view) {
					prev?.set(
						Configure.userID,
						prevRoomViewList.concat({ roomId: payload.roomId, deviceKey: payload.deviceKey })
					);
				} else {
					prev?.set(
						Configure.userID,
						prevRoomViewList.filter((item) => item.roomId !== payload.roomId)
					);
				}
			}

			return prev;
		});
	};

	const roomOwnerChanged = (payload: ChatServices.Server.RoomOwnerChanged.Data) => {
		const { spaceId, roomId, ownerId } = payload;
		if (spaceId) {
			setSpaceChatRoomList((prev) => {
				if (!prev) return prev;
				const space = prev.get(spaceId);
				if (!space) return prev;
				const room = space.rooms.find((room) => room.roomId === roomId);
				if (!room) return prev;
				room.ownerId = ownerId;
				return new Map(prev);
			});
		} else {
			setChatRoomList((prev) => {
				if (!prev) return prev;
				const prevRoom = prev.get(roomId);
				if (!prevRoom) return prev;
				prev.set(roomId, {
					...prevRoom,
					ownerId: ownerId
				});
				return new Map(prev);
			});
		}
	};

	return {
		roomCreated,
		groupDelete,
		memberNotificationUpdated,
		memberAdded,
		roomDeleted,
		messageSent,
		memberLeft,
		voiceCallStatusUpdated,
		roomNameUpdated,
		messageRead,
		messageReadAll,
		memberRoomPinned,
		memberRoomHided,
		roomNoticeNotified,
		userUpdated,
		groupWithdrawn,
		userBlock,
		addSpaceChannel,
		deleteSpaceChannel,
		spaceInfoUpdated,
		myRoomUpdated,
		spaceCreated,
		spaceDeleted,
		spaceInviteProcessed,
		roomViewUpdated,
		roomNoticeUpdate,
		roomOwnerChanged
	};
}
